import { useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, Collapse, Divider, FormControlLabel, Stack, Typography, useTheme } from "@mui/material";
import {
    kayttokohdeOptions,
    lujuusluokkaOptions,
    raekokoOptions,
    notkeusOptions,
    KAYTTO_IKA_ID_100V,
    labelFor,
} from "src/utils/quote/quoteConstants";
import { rasitusluokkaPresets } from "src/utils/quote/rasitusluokat";
import { gate1ContentFallback } from "src/utils/legal/acceptanceCopy";
import { isZeroValue } from "src/utils/quote/quoteValidation";
import { displayAddress } from "src/utils/quote/quoteAddress";
import StepHeader from "./StepHeader";
import SpecRow from "./SpecRow";
import SectionHeader from "./SectionHeader";
import LaskutusSection from "./LaskutusSection";
import { localizeDecimal } from "src/utils/valut/valutFormatters";
import { useLang, useT } from "src/i18n";

// Render numbers with a locale-aware decimal separator so the review matches
// the locale-formatted wizard inputs (the m³/kesto fields show "3,25" in
// Finnish, "3.25" in English). These are draft field strings with no fixed
// decimal count of our own choosing, so this delegates to localizeDecimal
// (separator swap only) rather than formatNumber (which would pin/pad the
// digit count and could add decimals that were never there).
const fi = (v, lang) => (v == null || v === "" ? "" : localizeDecimal(v, lang));

function GuidedBadge() {
    const theme = useTheme();
    const amber = theme.custom?.wizard?.amber ?? "#E8893B";
    const t = useT();
    return (
        <Chip
            label={t("wizard.stepReview.guidedBadge")}
            size="small"
            variant="outlined"
            sx={{
                borderColor: `${amber}66`,
                color: amber,
                fontSize: 11,
                letterSpacing: "0.04em",
                fontWeight: 500,
                bgcolor: `${amber}10`,
                height: 22,
            }}
        />
    );
}

function Section({ title, badge, onEdit, children }) {
    return (
        <Box>
            <SectionHeader title={title} badge={badge} onEdit={onEdit} />
            <Stack spacing={0.5}>{children}</Stack>
        </Box>
    );
}

function LegalAcceptance({ accepted, errorMessage, onToggle, checkboxLabel, details }) {
    const [open, setOpen] = useState(false);
    const t = useT();
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: errorMessage ? "error.main" : "divider",
                bgcolor: "rgba(244,242,238,0.02)",
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={accepted}
                        onChange={(e) => onToggle(e.target.checked)}
                        sx={{ alignSelf: "flex-start", mt: -0.5 }}
                    />
                }
                label={
                    <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.55 }}>
                        {checkboxLabel}
                    </Typography>
                }
                sx={{ alignItems: "flex-start", mr: 0 }}
            />
            <Box sx={{ pl: 4, mt: 0.5 }}>
                {errorMessage && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mb: 0.5 }}>
                        {t(errorMessage)}
                    </Typography>
                )}
                <Button
                    size="small"
                    onClick={() => setOpen((v) => !v)}
                    sx={{
                        textTransform: "none",
                        letterSpacing: 0,
                        color: "text.secondary",
                        px: 0,
                        "&:hover": { backgroundColor: "transparent", color: "text.primary" },
                    }}
                >
                    {open ? t("common.dialog.close") : t("wizard.stepReview.readMore")}
                </Button>
                <Collapse in={open} unmountOnExit>
                    <Stack spacing={1.25} sx={{ pt: 1, pb: 0.5 }}>
                        {details.map((p, i) => (
                            <Typography key={i} variant="body2" sx={{ fontSize: 12.5, lineHeight: 1.6, color: "text.secondary" }}>
                                {p}
                            </Typography>
                        ))}
                    </Stack>
                </Collapse>
            </Box>
        </Box>
    );
}

export default function StepReview({
    draft,
    errors,
    asiakases,
    selectedAsiakasId,
    onSelectAsiakas,
    onCreateCompany,
    onJumpToStep,
    onChange,
    onEditPerson,
    onEditSelectedAsiakas,
    onLaskutusEditingChange,
    isAuthenticated,
    person,
    attachments,
    consentCopy,
}) {
    const t = useT();
    const { lang } = useLang();
    // Defensive fallback — RequestForm always passes a real consentCopy (from its
    // own useAcceptanceCopy hook), so this only guards a caller that forgets to.
    const fallbackConsentCopy = useMemo(() => gate1ContentFallback(t), [t]);
    const effectiveConsentCopy = consentCopy ?? fallbackConsentCopy;
    const rasitusluokkaLabelFor = useMemo(() => {
        const presets = rasitusluokkaPresets(t);
        return (value) => labelFor(presets, value).split(" — ")[0];
    }, [t]);
    const kayttokohdeOpts = useMemo(() => kayttokohdeOptions(t), [t]);
    const lujuusOptions = useMemo(() => lujuusluokkaOptions(t), [t]);
    const raekokoOpts = useMemo(() => raekokoOptions(t), [t]);
    const notkeusOpts = useMemo(() => notkeusOptions(t), [t]);
    return (
        <Stack spacing={3} divider={<Divider flexItem sx={{ opacity: 0.4 }} />}>
            <StepHeader>{t("wizard.stepReview.title")}</StepHeader>

            <Section title={t("wizard.stepReview.sectionAddress")} onEdit={() => onJumpToStep(0)}>
                <SpecRow label={t("wizard.stepReview.addressLabel")} value={displayAddress(draft.formattedAddress || draft.osoite)} />
                {draft.feasibility?.deliverable === false && (
                    <Typography variant="caption" sx={{ color: "warning.main" }}>
                        {t("wizard.stepReview.noSupplyWarning")}
                    </Typography>
                )}
            </Section>

            <Section title={t("wizard.stepPump.title")} onEdit={() => onJumpToStep(1)}>
                <SpecRow
                    label={t("wizard.stepPump.boomFieldLabel")}
                    value={
                        isZeroValue(draft.puomi)
                            ? t("wizard.stepReview.boomFromRear")
                            : draft.puomi ? `${fi(draft.puomi, lang)} m` : t("wizard.stepReview.providerEstimates")
                    }
                />
                <SpecRow
                    label={t("wizard.stepPump.linjaLabel")}
                    value={
                        isZeroValue(draft.linjanPituus)
                            ? t("wizard.stepReview.linjaFromTip")
                            : draft.linjanPituus ? `${fi(draft.linjanPituus, lang)} m` : t("wizard.stepReview.providerEstimates")
                    }
                />
            </Section>

            <Section
                title={t("wizard.stepConcrete.title")}
                badge={draft.specMode === "guided" ? <GuidedBadge /> : null}
                onEdit={() => onJumpToStep(2)}
            >
                <SpecRow label={t("wizard.stepReview.amountLabel")} value={draft.maaraM3 ? `${fi(draft.maaraM3, lang)} m³` : ""} />
                <SpecRow label={t("wizard.stepReview.kestoLabel")} value={draft.kesto ? `${fi(draft.kesto, lang)} h` : ""} />
                <SpecRow label={t("wizard.shared.kayttokohdeLabel")} value={labelFor(kayttokohdeOpts, draft.kayttokohde)} />
                <SpecRow label={t("wizard.shared.rasitusluokkaLabel")} value={draft.rasitusluokka ? rasitusluokkaLabelFor(draft.rasitusluokka) : ""} />
                <SpecRow label={t("wizard.stepReview.lujuusLabel")} value={labelFor(lujuusOptions, draft.lujuusId)} />
                <SpecRow label={t("wizard.stepConcrete.raekokoLabel")} value={labelFor(raekokoOpts, draft.raeKokoId)} />
                <SpecRow label={t("wizard.stepConcrete.notkeusLabel")} value={labelFor(notkeusOpts, draft.notkeusId)} />
                {draft.specMode != null && draft.kayttoIkaId != null && (
                    <SpecRow label={t("wizard.shared.suunnitteluikaLabel")} value={draft.kayttoIkaId === KAYTTO_IKA_ID_100V ? t("wizard.shared.years100") : t("wizard.shared.years50")} />
                )}
                {draft.betonikuvaus && <SpecRow label={t("wizard.stepReview.descriptionLabel")} value={draft.betonikuvaus} />}
            </Section>

            <Section title={t("wizard.stepDetails.title")} onEdit={() => onJumpToStep(3)}>
                {draft.ajoOhjeet && <SpecRow label={t("wizard.stepDetails.driveLabel")} value={draft.ajoOhjeet} />}
                {draft.kuvaus && <SpecRow label={t("wizard.stepDetails.otherLabel")} value={draft.kuvaus} />}
                {attachments?.length > 0 && <SpecRow label={t("wizard.stepReview.attachmentsLabel")} value={t("wizard.stepReview.attachmentsCount", { n: attachments.length })} />}
                {!draft.ajoOhjeet && !draft.kuvaus && !(attachments?.length > 0) && (
                    <Typography variant="body2" sx={{ fontSize: 13, color: "text.secondary" }}>
                        {t("wizard.stepReview.noDetails")}
                    </Typography>
                )}
            </Section>

            {/* The "send tail" (contact / billing / Gate-1 terms) renders for
                everyone as a keyed ARRAY (not a Fragment) so MUI's Stack divider
                still interleaves between the sections — React.Children.toArray
                flattens arrays but treats a Fragment as a single child. In demo
                mode an extra anon-only entry appends the demo notice. */}
            {[
                <Section key="yhteystiedot" title={t("wizard.stepReview.sectionContact")} onEdit={isAuthenticated && person ? onEditPerson : undefined}>
                    {isAuthenticated && person ? (
                        <>
                            {person.fullName && <SpecRow label={t("wizard.stepReview.nameLabel")} value={person.fullName} />}
                            {person.phone && <SpecRow label={t("wizard.stepReview.phoneLabel")} value={person.phone} />}
                            {person.email && <SpecRow label={t("wizard.stepReview.emailLabel")} value={person.email} />}
                        </>
                    ) : (
                        <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.55, color: "text.secondary" }}>
                            {t("wizard.stepReview.notAuthenticated")}
                        </Typography>
                    )}
                </Section>,
                isAuthenticated && (
                    <LaskutusSection
                        key="laskutus"
                        asiakases={asiakases}
                        selectedAsiakasId={selectedAsiakasId}
                        onSelectAsiakas={onSelectAsiakas}
                        onCreateCompany={onCreateCompany}
                        onEditSelected={onEditSelectedAsiakas}
                        onEditingChange={onLaskutusEditingChange}
                        person={person}
                        errors={errors}
                    />
                ),
                isAuthenticated && (
                    <LegalAcceptance
                        key="legal"
                        accepted={!!draft.termsAccepted}
                        errorMessage={errors?.termsAccepted}
                        onToggle={(checked) => onChange({ termsAccepted: checked })}
                        checkboxLabel={effectiveConsentCopy.checkboxLabel}
                        details={effectiveConsentCopy.details}
                    />
                ),
            ]}
        </Stack>
    );
}
